import React, { useState, useEffect, useCallback } from 'react';
import { Form, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import createInvoiceItemColumns from '../Utils/InvoiceItemColumns';
import InvoiceFormLayout from './InvoiceFormLayout';
import { 
  getAllPartners, getAllProducts, getAllCategories, getAccountTree,
  getInvoiceById, createInvoiceWithFile, updateInvoiceWithFile, getMailingOffers
} from '../../../api/accounting';
import { getLists, getSegments } from '../../../../../core/api/mediaplan';
import { getOffersByBrand, getMarketingFilesByBrand } from '../../../../../core/api/marketing';
import dayjs from 'dayjs';
import { 
  calculateTotals, 
  handleProductChange as handleProductChangeUtil,
  handleItemChange as handleItemChangeUtil,
  formatInvoiceData,
  prepareFormData
} from '../../../utils/invoiceUtils';

const CreateInvoice = ({ selectedBrand, invoiceId }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [partners, setPartners] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [offers, setOffers] = useState([]);
  const [mailingOffers, setMailingOffers] = useState([]);
  const [marketingFiles, setMarketingFiles] = useState([]);
  const [lists, setLists] = useState([]);
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({
    calculated_subtotal: 0,
    calculated_taxAmount: 0,
    calculated_discountAmount: 0,
    calculated_total: 0,
    match: true
  });

  // State to track if we're in edit mode
  const [isEditMode, setIsEditMode] = useState(false);
  
  // State for multiple payment terms
  const [paymentTerms, setPaymentTerms] = useState([]);
  const INVOICE_DRAFT_KEY = 'invoiceDraft';
const debounce = (fn, delay) => {
  let timer;
  return (...args) => {
    clearTimeout(timer);
    timer = setTimeout(() => fn(...args), delay);
  };
};
useEffect(() => {
  if (!isEditMode && selectedBrand) {
    const saveDraft = debounce(() => {
      const values = form.getFieldsValue();
      const draft = {
        formValues: values,
        invoiceItems,
        paymentTerms,
      };
      sessionStorage.setItem(INVOICE_DRAFT_KEY, JSON.stringify(draft));
      console.log('💾 Invoice draft saved');
    }, 500);

    saveDraft();
  }
}, [form, invoiceItems, paymentTerms, isEditMode, selectedBrand]);
useEffect(() => {
  if (!isEditMode && selectedBrand) {
    const draft = sessionStorage.getItem(INVOICE_DRAFT_KEY);
    if (draft) {
      try {
        const parsed = JSON.parse(draft);
        console.log('📥 Restoring invoice draft:', parsed);

        if (parsed.formValues) {
          // Format dates
          if (parsed.formValues.issueDate) {
            parsed.formValues.issueDate = dayjs(parsed.formValues.issueDate);
          }
          if (parsed.formValues.dueDate) {
            parsed.formValues.dueDate = dayjs(parsed.formValues.dueDate);
          }

          form.setFieldsValue(parsed.formValues);
        }
        if (parsed.invoiceItems) setInvoiceItems(parsed.invoiceItems);
        if (parsed.paymentTerms) setPaymentTerms(parsed.paymentTerms);
      } catch (e) {
        console.warn('❌ Failed to parse invoice draft:', e);
      }
    }
  }
}, [isEditMode, selectedBrand, form]);

  
// Data fetching functions
const fetchPartners = useCallback(async () => {
    try {
      // Add brand filter if a brand is selected
      const filters = selectedBrand ? { brand: selectedBrand.id } : {};
      const response = await getAllPartners(filters);
      setPartners(response.data);
    } catch (error) {
      console.error('Error fetching partners:', error.response || error);
      message.error('Failed to fetch partners');
    }
  }, [selectedBrand, setPartners]);

  const fetchCategories = useCallback(async () => {
    try {
      // Add brand filter if a brand is selected
      const filters = selectedBrand ? { brand: selectedBrand.id } : {};
      const response = await getAllCategories(filters);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error.response || error);
      message.error('Failed to fetch categories');
    }
  }, [selectedBrand, setCategories]);
  
  const fetchAccounts = useCallback(async () => {
    try {
      // Add brand filter if a brand is selected
      const filters = selectedBrand ? { brand: selectedBrand.id } : {};
      // Use account tree API to get hierarchical structure
      const response = await getAccountTree(filters);
      setAccounts(response);
    } catch (error) {
      console.error('Error fetching account tree:', error.response || error);
      message.error('Failed to fetch chart of accounts');
    }
  }, [selectedBrand, setAccounts]);

  const fetchProducts = useCallback(async (vendorId = null) => {
    try {
      // Add brand and vendor filters if selected
      const filters = {};
      if (selectedBrand) {
        filters.brand = selectedBrand.id;
      }
      if (vendorId) {
        filters.vendor = vendorId;
      }
      const response = await getAllProducts(filters);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error.response || error);
      message.error('Failed to fetch products');
    }
  }, [selectedBrand, setProducts]);

  const fetchOffers = useCallback(async () => {
    try {
      if (!selectedBrand) {
        setOffers([]);
        return;
      }
      const res = await getOffersByBrand(selectedBrand.id);
      // Some APIs return {data: []}, others return [] directly; normalize
      const list = Array.isArray(res?.data) ? res.data : res;
      setOffers(list || []);
    } catch (error) {
      console.error('Error fetching offers:', error.response || error);
      message.error('Failed to fetch offers');
    }
  }, [selectedBrand]);
  
  // Fetch mailing offers for print type
  const fetchMailingOffers = useCallback(async () => {
    try {
      if (!selectedBrand) {
        setMailingOffers([]);
        return;
      }
      const filters = { brand: selectedBrand.id };
      const response = await getMailingOffers(filters);
      setMailingOffers(response.data || []);
    } catch (error) {
      console.error('Error fetching mailing offers:', error.response || error);
      message.error('Failed to fetch mailing offers');
    }
  }, [selectedBrand]);

  const fetchLists = useCallback(async () => {
    try {
      const response = await getLists();
      setLists(response.data || []);
      setSegments([]); // Clear segments when lists are fetched
    } catch (error) {
      console.error('Error fetching lists:', error.response || error);
      message.error('Failed to fetch lists');
    }
  }, []);

  const fetchSegments = useCallback(async (listId) => {
    try {
      const response = await getSegments({ list: listId });
      setSegments(response.data || []);
    } catch (error) {
      console.error('Error fetching segments:', error.response || error);
      message.error('Failed to fetch segments');
    }
  }, []);

  // Fetch marketing files by brand
  const fetchMarketingFiles = useCallback(async () => {
    try {
      if (!selectedBrand) {
        setMarketingFiles([]);
        return;
      }
      const res = await getMarketingFilesByBrand(selectedBrand.id);
      const list = Array.isArray(res?.data) ? res.data : res;
      setMarketingFiles(list || []);
    } catch (error) {
      console.error('Error fetching marketing files:', error.response || error);
      message.error('Failed to fetch marketing files');
    }
  }, [selectedBrand]);

  // Removed duplicate fetchCategories function

  const handlePartnerChange = (value) => {
    const selectedPartner = partners.find(p => p.id === value);
    if (selectedPartner) {
      // If the selected partner is a vendor, fetch products and categories for this vendor
      if (selectedPartner.partner_type === 'vendor' || selectedPartner.partner_type === 'both') {
        fetchProducts(selectedPartner.id);
        fetchCategories(selectedPartner.id);
      } else {
        // If not a vendor, fetch all products and categories for the selected brand
        fetchProducts();
        fetchCategories();
      }
    }
  };

  // Payment terms management
    const addPaymentTerm = () => {
    const newTermsCount = paymentTerms.length + 1;
    const evenPercentage = Math.floor(100 / newTermsCount);
    const remainder = 100 % newTermsCount;

    const formTotalAmount = form.getFieldValue('total_amount') || 0;

    // Rebuild all terms with updated percentage
    const updatedTerms = paymentTerms.map((term, index) => {
      const percent = evenPercentage + (index === newTermsCount - 1 ? remainder : 0);
      return {
        ...term,
        percentage: percent,
        amount: (percent / 100) * formTotalAmount,
        due_days: 30 * (index + 1)
      };
    });

    // Add the new term
    const newPercentage = evenPercentage + (paymentTerms.length === newTermsCount - 1 ? remainder : 0);
    updatedTerms.push({
      id: Date.now(), // Temporary ID
      code: '',
      description: '',
      percentage: newPercentage,
      due_days: 30 * newTermsCount,
      amount: (newPercentage / 100) * formTotalAmount,
      active: true
    });

    setPaymentTerms(updatedTerms);
  };

    
  const removePaymentTerm = (id) => {
    setPaymentTerms(paymentTerms.filter(term => term.id !== id));
  };

  const updatePaymentTerm = (id, field, value) => {
    // If updating percentage, check if it would exceed 100% total
    if (field === 'percentage') {
      // Calculate the total percentage excluding the current term
      const otherTermsTotal = paymentTerms.reduce((sum, term) => {
        return term.id !== id ? sum + parseFloat(term.percentage || 0) : sum;
      }, 0);
      
      // Check if the new total would exceed 100%
      if (otherTermsTotal + parseFloat(value || 0) > 100) {
        // Calculate the maximum allowed percentage for this term
        const maxAllowed = 100 - otherTermsTotal;
        message.warning(`Total percentage cannot exceed 100%. Setting to maximum allowed: ${maxAllowed.toFixed(2)}%`);
        value = maxAllowed;
      }
    }
    
    // Get the current total_amount from the form
    const formTotalAmount = form.getFieldValue('total_amount') || 0;
    
    const updatedTerms = paymentTerms.map(term => {
      if (term.id === id) {
        const updatedTerm = { ...term, [field]: value };
        
        // If percentage is being updated, recalculate the amount
        if (field === 'percentage') {
          updatedTerm.amount = (value / 100) * formTotalAmount;
        }
        
        return updatedTerm;
      }
      return term;
    });
    setPaymentTerms(updatedTerms);
  };




  // Fetch invoice data for editing
  const fetchInvoiceData = useCallback(async (id) => {
    try {
      setLoading(true);
      const invoiceData = await getInvoiceById(id);
 
      
      // Format the data for the form
      const formattedItems = invoiceData.items.map(item => {
        // Derive offer details and first product variation if available
        const offerDetails = item.offer_details || {};
        const variations = Array.isArray(offerDetails.product_variations_detail)
          ? offerDetails.product_variations_detail
          : [];
        const firstVariation = variations.length > 0 ? variations[0] : null;

        // Prefer explicit fields; fallback to offer details/variation meta
        const derivedProductName = item.product_name
          || firstVariation?.name
          || offerDetails.name
          || '';

        const derivedProductVariation = item.product_variation
          || firstVariation?.description
          || offerDetails.product_variation
          || offerDetails.variation
          || item.description
          || '';

        // Prefer item.product; fallback to variation's product_id if present
        const derivedProductId = item.product || firstVariation?.product_id || null;

        const normalizedOfferId = item.offer || item.offer_id || offerDetails?.id || null;

        // Get product type fields with defaults
        const productType = item.product_type || 'product';
        const printType = item.print_type || null;
        const envelope = item.envelope || null;
        const marketingOffer = item.marketing_offer || null;

        return {
          key: item.id,
          productId: derivedProductId,
          categoryId: item.category,
          accountId: item.account || item.account_id || null,
          productName: derivedProductName,
          productVariation: derivedProductVariation,
          description: item.description,
          quantity: item.quantity,
          unitPrice: item.unit_price,
          taxRate: item.tax_rate,
          taxAmount: item.tax_amount,
          discountAmount: item.discount_amount || 0,
          subtotal: item.subtotal,
          total_amount: item.total,
          // Normalize offer
          offerId: normalizedOfferId,
          offerIds: normalizedOfferId ? [normalizedOfferId] : [],
          // Add product type fields
          productType,
          printType,
          envelope,
          marketingOffer,
          // Add list and segment IDs
          listId: item.list || null,
          segmentId: item.segment || null,
        };
      });
      
      // Set form values first so calculateTotals can access them
      form.setFieldsValue({
        partner: invoiceData.partner,
        issueDate: dayjs(invoiceData.issue_date),
        dueDate: dayjs(invoiceData.due_date),
        invoiceType: invoiceData.invoice_type,
        number: invoiceData.number,
        currency: invoiceData.currency,
        status: invoiceData.status,
        notes: invoiceData.notes,
        subtotal: invoiceData.subtotal || 0,
        discount_amount: invoiceData.discount_amount || 0,
        tax_rate: invoiceData.tax_rate || 0,
        total_amount: invoiceData.total_amount || 0,
        // Handle file URL if present
        fileUrl: invoiceData.file || null,
      });
      
      setInvoiceItems(formattedItems);

      // Pre-load segments for all lists present in the invoice items
      if (formattedItems.length > 0) {
        const listIds = [...new Set(formattedItems.map(item => item.listId).filter(id => id))];
        if (listIds.length > 0) {
          Promise.all(listIds.map(id => getSegments({ list: id })))
            .then(results => {
              // results is an array of responses, flatten the data arrays
              const allSegments = results.flatMap(res => res.data || []);
              setSegments(allSegments);
            })
            .catch(error => {
              console.error('Error fetching segments for invoice lists:', error);
              message.error('Failed to pre-load segment data.');
            });
        }
      }
      
      // Calculate values using the same utility as when creating invoice
      const calculatedValues = calculateTotals(formattedItems, form);
      setCalculatedValues(calculatedValues);
      
      // Load payment terms associated with this invoice and recalculate amounts
      if (invoiceData.payment_terms && Array.isArray(invoiceData.payment_terms)) {
        // Get the form's total_amount value
        const formTotalAmount = form.getFieldValue('total_amount') || 0;
        
        // Recalculate payment term amounts based on the form's total_amount
        const updatedPaymentTerms = invoiceData.payment_terms.map(term => {
          // Calculate amount based on percentage and the form's total_amount
          const amount = (term.percentage / 100) * formTotalAmount;
          return { ...term, amount };
        });
        
        setPaymentTerms(updatedPaymentTerms);
      }
    } catch (error) {
      console.error('Error fetching invoice data:', error);
      message.error('Failed to fetch invoice data');
    } finally {
      setLoading(false);
    }
  }, [form, setInvoiceItems, setCalculatedValues, setLoading, setPaymentTerms]);


  // Invoice items management
  const addItem = () => {
    setInvoiceItems([
      ...invoiceItems,
      {
        key: Date.now(),
        productId: null,
        categoryId: null,
        accountId: null,
        productName: '',
        productVariation: '',
        description: '',
        quantity: 1,
        unitPrice: 0,
        taxRate: 0,
        taxAmount: 0,
        discountAmount: 0,
        subtotal: 0,
        total: 0,
        offerId: null,
        offerIds: [],
        // Add default values for product type fields
        productType: 'product', // Default to 'product' type
        printType: null,        // Will be set when productType is 'print'
        envelope: null,         // Will be set when printType is 'Envelope'
        marketingOffer: null,   // Will be set when printType is 'Marketing'
        listId: null,
        segmentId: null,
      },
    ]);
  };

  const removeItem = (key) => {
    const updatedItems = invoiceItems.filter(item => item.key !== key);
    setInvoiceItems(updatedItems);
    calculateInvoiceTotals(updatedItems);
  };

  const handleProductChange = (value, key) => {
    const updatedItems = handleProductChangeUtil(value, key, products, invoiceItems);
    if (updatedItems !== invoiceItems) {
      setInvoiceItems(updatedItems);
      calculateInvoiceTotals(updatedItems);
    }
  };

  // const handleItemChange = (key, field, value) => {
  //   const updatedItems = handleItemChangeUtil(key, field, value, invoiceItems);
  //   setInvoiceItems(updatedItems);
  //   calculateInvoiceTotals(updatedItems);
  // };
  const handleItemChange = (key, field, value) => {
    console.log(`🔄 handleItemChange called: key=${key}, field=${field}, value=${value}`);
    console.log('🔍 Current invoiceItems before update:', invoiceItems);
    
    const updatedItems = handleItemChangeUtil(key, field, value, invoiceItems);
    console.log('📊 Updated items after util:', updatedItems);
    
    // Check if the specific item was actually updated
    const updatedItem = updatedItems.find(item => item.key === key);
    console.log(`🎯 Updated item for key ${key}:`, updatedItem);
    
    setInvoiceItems(updatedItems);
    calculateInvoiceTotals(updatedItems);
  };
const clearDraft = () => {
  sessionStorage.removeItem(INVOICE_DRAFT_KEY);
  console.log('🗑️ Invoice draft cleared');
};


  const handleInvoiceError = (error, isEditMode) => {
    const action = isEditMode ? 'update' : 'create';
  
    // 1. Top‐level detail error
    if (error.response?.data?.detail) {
      message.error(`Failed to ${action} invoice: ${error.response.data.detail}`);
      return;
    }
  
    // 2. Field‐level validation errors
    const errs = error.response?.data;
    if (errs && typeof errs === 'object') {
      Object.entries(errs).forEach(([field, messages]) => {
        messages.forEach(msg => {
          message.error(`${field}: ${msg}`);
        });
      });
      return;
    }
  
    // 3. Fallback
    message.error(`Failed to ${action} invoice: ${error.message}`);
  }
  const calculateInvoiceTotals = useCallback((items) => {
    const calculatedValues = calculateTotals(items, form);
    setCalculatedValues(calculatedValues);
    
    // Update form fields with calculated values
    // form.setFieldsValue({
    //   subtotal: calculatedValues.subtotal,
    //   discount_amount: calculatedValues.discount_amount,
    //   tax_rate: calculatedValues.tax_rate,
    //   total_amount: calculatedValues.calculated_total,
    // });
    
    // Get the current total_amount from the form
    const formTotalAmount = form.getFieldValue('total_amount') || 0;
    
    // Update payment term amounts if percentage changes
    if (paymentTerms.length > 0) {
      const updatedTerms = paymentTerms.map(term => {
        return {
          ...term,
          amount: (term.percentage / 100) * formTotalAmount
        };
      });
      setPaymentTerms(updatedTerms);
    }
  }, [form, paymentTerms]);

  const onFinish = async (values) => {
    if (!selectedBrand) {
      message.error('Please select a brand before saving the invoice');
      return;
    }

    // Validate payment terms
    if (paymentTerms.length === 0) {
      message.error('Please add at least one payment term');
      return;
    }

    // Check if payment term percentages add up to 100%
    const totalPercentage = paymentTerms.reduce((sum, term) => sum + parseFloat(term.percentage || 0), 0);
    if (Math.abs(totalPercentage - 100) > 0.01) { // Allow small floating point error
      message.error(`Payment term percentages must add up to 100%. Current total: ${totalPercentage}%`);
      return;
    }

    // Check if total amount matches calculated total
    const formTotalAmount = parseFloat(form.getFieldValue('total_amount') || 0);
    const calculatedTotal = calculatedValues.calculated_total;
    
    // If totals don't match, show confirmation dialog instead of preventing submission
    if (Math.abs(formTotalAmount - calculatedTotal) > 0.01) {
      const { Modal } = await import('antd');
      
      Modal.confirm({
        title: 'Total Amount Mismatch',
        content: `The total amount (${formTotalAmount}) doesn't match the calculated total (${calculatedTotal}). Do you want to proceed anyway?`,
        okText: 'Yes, proceed',
        cancelText: "No, I'll fix it",
        onOk: async () => {
          // Continue with submission if user confirms
          await submitInvoice(values);
        },
      });
      return;
    }
    
    // If totals match, proceed with submission
    await submitInvoice(values);
  };
  
  // Helper function to submit the invoice
  const submitInvoice = async (values) => {
    setLoading(true);
    try {
      // Format invoice data
      const invoiceData = formatInvoiceData(values, calculatedValues, invoiceItems, selectedBrand);
      console.log('Invoice data: before sending', invoiceData, values);

      // Prepare form data for submission
      const formData = prepareFormData(invoiceData, values);
      
      // Add payment terms as JSON string
      formData.append('payment_terms', JSON.stringify(paymentTerms));
      
      if (isEditMode) {
        await updateInvoiceWithFile(invoiceId, formData);
        message.success('Invoice updated successfully');
        clearDraft();

      } else {
        // Use the new v2 endpoint
        await createInvoiceWithFile(formData, 'v2');
        message.success('Invoice created successfully');
        clearDraft();

      }
      
      navigate('/accounting/invoices');
    } catch (error) {
      handleInvoiceError(error, isEditMode);
    } finally {
      setLoading(false);
    }
  };

  const handleFormValuesChange = useCallback((changedValues) => {
    // Still need to calculate invoice totals if these specific fields change
    if ('discount_amount' in changedValues || 'tax_rate' in changedValues || 'total_amount' in changedValues) {
      calculateInvoiceTotals(invoiceItems);
    }
    
    // If issue date changes, force a re-render of payment terms to update due dates
    if ('issueDate' in changedValues && paymentTerms.length > 0) {
      // Create a copy of payment terms to trigger re-render
      const updatedTerms = [...paymentTerms];
      setPaymentTerms(updatedTerms);
    }
    
    // Recalculate payment terms for any form value change
    if (paymentTerms.length > 0) {
      // Get the current total_amount from the form
      const formTotalAmount = form.getFieldValue('total_amount') || 0;
      
      // Update all payment terms based on their percentages
      const updatedTerms = paymentTerms.map(term => {
        return {
          ...term,
          amount: (term.percentage / 100) * formTotalAmount
        };
      });
      
      setPaymentTerms(updatedTerms);
    }
  }, [calculateInvoiceTotals, invoiceItems, paymentTerms, form]);

  const getCurrentCurrency = () => form.getFieldValue('currency') || '$';

  const columns = createInvoiceItemColumns(
    products, 
    categories, 
    accounts,
    offers,
    handleProductChange, 
    handleItemChange, 
    getCurrentCurrency,
    setInvoiceItems,
    calculateInvoiceTotals,
    removeItem,
    getCurrentCurrency(),
    mailingOffers,
    marketingFiles,
    invoiceItems,
    lists,
    segments,
    fetchSegments,
    navigate
  );


  useEffect(() => {
    if (selectedBrand) {
      fetchPartners();
      fetchCategories();
      fetchAccounts();
      fetchOffers();
      fetchMailingOffers();
      fetchMarketingFiles();
      fetchLists();
      
      // If invoiceId is provided, fetch the invoice data
      if (invoiceId) {
        setIsEditMode(true);
        fetchInvoiceData(invoiceId);
      }
    } else {
      // Clear all data when no brand is selected
      setPartners([]);
      setProducts([]);
      setCategories([]);
      setAccounts([]);
      setOffers([]);
      setMailingOffers([]);
      setMarketingFiles([]);
      setLists([]);
      setSegments([]);
    }
  }, [selectedBrand, invoiceId, fetchPartners, fetchCategories, fetchAccounts, fetchOffers, fetchMailingOffers, fetchMarketingFiles, fetchInvoiceData, fetchLists]);
  
  

  return (
    <InvoiceFormLayout
      form={form}
      isEditMode={isEditMode}
      loading={loading}
      invoiceItems={invoiceItems}
      partners={partners}
      paymentTerms={paymentTerms}
      categories={categories}
      columns={columns}
      calculatedValues={calculatedValues}
      onFinish={onFinish}
      handlePartnerChange={handlePartnerChange}
      addItem={addItem}
      addPaymentTerm={addPaymentTerm}
      removePaymentTerm={removePaymentTerm}
      updatePaymentTerm={updatePaymentTerm}
      navigate={navigate}
      selectedBrand={selectedBrand}
      onValuesChange={handleFormValuesChange}
      marketingFiles={marketingFiles}
    />
  );
};

export default CreateInvoice;